/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlinePhotograph, HiOutlinePencil, HiOutlineTrash } from "react-icons/hi";

import ListPage from "../../layout/ListPage";
import type { Action, Column } from "../../ui";
import { usePlugImageContext } from "../../context/PlugImageContext";
import { pushOnePlugImage } from "../../lib/sysNavigation/SysNavigation";
import { _PAGE_SIZE_ } from "../../lib/other/tools";

type PlugImage = {
  plug_image_id: number | string;
  name?: string;
  title?: string;
  thumbnail?: string;
  [key: string]: unknown;
};

const addRoute = "/plug/image/add";
const editRoute = "/plug/image/edit";

const Overview: React.FC = () => {
  const navigate = useNavigate();
  const {
    plugImage,
    count,
    fetchPlugImages,
    deletePlugImage,
    updatePlugImageState,
  } = usePlugImageContext();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!plugImage) fetchPlugImages(page, pageSize);
  }, [plugImage]);

  const rows = useMemo<PlugImage[] | null>(() => {
    if (!Array.isArray(plugImage)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return plugImage as PlugImage[];
    return (plugImage as PlugImage[]).filter((p) =>
      (p.name ?? p.title ?? "").toString().toLowerCase().includes(q),
    );
  }, [plugImage, query]);

  const columns: Column<PlugImage>[] = [
    {
      header: "Image",
      cell: (row) => (
        <div className="flex items-center gap-3">
          {row.thumbnail ? (
            <img
              src={row.thumbnail}
              alt={row.name ?? row.title ?? ""}
              className="h-10 w-16 rounded-md object-cover ring-1 ring-gray-200"
            />
          ) : (
            <span className="flex h-10 w-16 items-center justify-center rounded-md bg-brand/10 text-brand ring-1 ring-inset ring-brand/15">
              <HiOutlinePhotograph className="h-4 w-4" />
            </span>
          )}
          <span className="font-medium text-gray-900">
            {row.name ?? row.title ?? "—"}
          </span>
        </div>
      ),
    },
  ];

  const handleDelete = async (row: PlugImage) => {
    const response: any = await deletePlugImage(row.plug_image_id);
    if (response?.status === 200 && Array.isArray(plugImage)) {
      updatePlugImageState(
        (plugImage as PlugImage[]).filter(
          (p) => p.plug_image_id !== row.plug_image_id,
        ),
      );
    }
  };

  const rowActions = (row: PlugImage): Action[] => [
    {
      label: "Edit",
      icon: <HiOutlinePencil className="h-3.5 w-3.5" />,
      onClick: () => {
        pushOnePlugImage(row);
        navigate(editRoute);
      },
    },
    {
      label: "Delete",
      variant: "destructive",
      icon: <HiOutlineTrash className="h-3.5 w-3.5" />,
      onClick: () => handleDelete(row),
      confirm: {
        title: `Delete this plug image?`,
        description: "The image will be permanently removed.",
        confirmLabel: "Delete",
      },
    },
  ];

  return (
    <ListPage<PlugImage>
      title="Plug Images"
      subtitle="Promo images used across the public site."
      icon={<HiOutlinePhotograph className="h-5 w-5" />}
      actions={[{ label: "Add plug image", variant: "primary", href: addRoute }]}
      rows={rows}
      columns={columns}
      rowKey={(r) => r.plug_image_id}
      rowActions={rowActions}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search plug images…",
      }}
      pagination={{
        page,
        pageSize,
        total: count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          fetchPlugImages(p, ps);
        },
      }}
      empty="No plug images yet."
    />
  );
};

export default Overview;
